import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { 
  AlertTriangle, TrendingUp, Target, BookOpen, Layers, 
  ChevronRight, Award, XCircle, CheckCircle2, Zap
} from "lucide-react";

const WeakAreas = ({ auth }) => {
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWeakAreas();
  }, []);

  const fetchWeakAreas = async () => {
    try {
      const response = await axios.get(`${API}/weak-areas`, {
        withCredentials: true,
        headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : {}
      });
      setAnalysis(response.data);
    } catch (error) {
      console.error("Error fetching weak areas:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "weak": return "text-red-600 bg-red-50 border-red-200";
      case "needs_improvement": return "text-yellow-600 bg-yellow-50 border-yellow-200";
      case "strong": return "text-green-600 bg-green-50 border-green-200";
      default: return "text-slate-600 bg-slate-50 border-slate-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "weak": return <XCircle className="w-5 h-5 text-red-500" />;
      case "needs_improvement": return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case "strong": return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      default: return null;
    }
  };

  const getProgressColor = (score) => {
    if (score < 60) return "bg-red-500";
    if (score < 75) return "bg-yellow-500";
    return "bg-green-500";
  };

  if (loading) {
    return (
      <DashboardLayout auth={auth}>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-maple border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!analysis?.has_data) {
    return (
      <DashboardLayout auth={auth}>
        <div className="max-w-2xl mx-auto text-center py-16" data-testid="weak-areas-empty">
          <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Target className="w-10 h-10 text-slate-400" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 mb-3">
            No Test Data Yet
          </h1>
          <p className="text-slate-600 mb-8 max-w-md mx-auto">
            Complete some practice tests or simulations to see your personalized weak area analysis and recommendations.
          </p>
          <div className="flex gap-4 justify-center">
            <Button onClick={() => navigate("/simulations")} className="bg-maple hover:bg-maple-600">
              <Zap className="w-4 h-4 mr-2" />
              Start Simulation
            </Button>
            <Button variant="outline" onClick={() => navigate("/mock-tests")}>
              <Target className="w-4 h-4 mr-2" />
              Take Mock Test
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout auth={auth}>
      <div className="space-y-8" data-testid="weak-areas-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Weak Areas Analysis</h1>
          <p className="text-slate-600">Personalized insights to improve your citizenship test preparation</p>
        </div>

        {/* Overview Stats */}
        <div className="grid md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm bg-gradient-to-br from-maple-50 to-red-50">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-maple">{analysis.tests_taken}</p>
              <p className="text-sm text-slate-600">Tests Taken</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-truenorth-50 to-blue-50">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-truenorth">{analysis.average_score}%</p>
              <p className="text-sm text-slate-600">Average Score</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-red-50 to-orange-50">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-red-600">{analysis.weak_areas?.length || 0}</p>
              <p className="text-sm text-slate-600">Weak Areas</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-emerald-50">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-600">{analysis.strong_areas?.length || 0}</p>
              <p className="text-sm text-slate-600">Strong Areas</p>
            </CardContent>
          </Card>
        </div>

        {/* Recommendations */}
        {analysis.recommendations?.length > 0 && (
          <Card className="border-2 border-maple-100 bg-maple-50/30">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-maple" />
                Personalized Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {analysis.recommendations.map((rec, idx) => (
                <div 
                  key={idx}
                  className={`flex items-center justify-between p-4 rounded-lg bg-white border ${
                    rec.priority === "high" ? "border-red-200" : "border-yellow-200"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      rec.priority === "high" ? "bg-red-100" : "bg-yellow-100"
                    }`}>
                      {rec.type === "flashcards" ? (
                        <Layers className="w-5 h-5 text-maple" />
                      ) : rec.type === "weak_chapter" ? (
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                      ) : (
                        <BookOpen className="w-5 h-5 text-yellow-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{rec.action}</p>
                      {rec.score !== undefined && (
                        <p className="text-sm text-slate-500">Current score: {rec.score}%</p>
                      )}
                    </div>
                  </div>
                  <Button 
                    size="sm"
                    onClick={() => navigate(rec.link)}
                    className={rec.priority === "high" ? "bg-red-600 hover:bg-red-700" : "bg-yellow-600 hover:bg-yellow-700"}
                  >
                    Practice <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Chapter Performance Grid */}
        <div className="space-y-4">
          <h2 className="font-heading text-xl font-semibold text-slate-900">Chapter Performance</h2>
          
          {/* Weak Areas */}
          {analysis.weak_areas?.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-red-600 flex items-center gap-2">
                <XCircle className="w-4 h-4" /> Weak Areas (Below 60%)
              </h3>
              <div className="grid md:grid-cols-2 gap-3">
                {analysis.weak_areas.map((chapter) => (
                  <Card key={chapter.chapter_id} className={`border-2 ${getStatusColor(chapter.status)}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(chapter.status)}
                          <div>
                            <h4 className="font-medium text-slate-900">{chapter.chapter_title}</h4>
                            <p className="text-xs text-slate-500">{chapter.correct}/{chapter.total} correct • {chapter.attempts} attempts</p>
                          </div>
                        </div>
                        <Badge className="bg-red-100 text-red-700">{chapter.score}%</Badge>
                      </div>
                      <Progress value={chapter.score} className="h-2" />
                      <div className="mt-3 flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => navigate("/chapter-practice")}
                          className="flex-1"
                        >
                          <BookOpen className="w-4 h-4 mr-1" /> Practice
                        </Button>
                        <Button 
                          size="sm"
                          onClick={() => navigate("/flashcards")}
                          className="flex-1 bg-red-600 hover:bg-red-700"
                        >
                          <Layers className="w-4 h-4 mr-1" /> Flashcards
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Needs Improvement */}
          {analysis.needs_improvement?.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-yellow-600 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Needs Improvement (60-74%)
              </h3>
              <div className="grid md:grid-cols-2 gap-3">
                {analysis.needs_improvement.map((chapter) => (
                  <Card key={chapter.chapter_id} className={`border ${getStatusColor(chapter.status)}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(chapter.status)}
                          <div>
                            <h4 className="font-medium text-slate-900">{chapter.chapter_title}</h4>
                            <p className="text-xs text-slate-500">{chapter.correct}/{chapter.total} correct</p>
                          </div>
                        </div>
                        <Badge className="bg-yellow-100 text-yellow-700">{chapter.score}%</Badge>
                      </div>
                      <Progress value={chapter.score} className="h-2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Strong Areas */}
          {analysis.strong_areas?.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-green-600 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Strong Areas (75%+)
              </h3>
              <div className="grid md:grid-cols-3 gap-3">
                {analysis.strong_areas.map((chapter) => (
                  <Card key={chapter.chapter_id} className={`border ${getStatusColor(chapter.status)}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Award className="w-5 h-5 text-green-500" />
                          <h4 className="font-medium text-slate-900 text-sm">{chapter.chapter_title}</h4>
                        </div>
                        <Badge className="bg-green-100 text-green-700">{chapter.score}%</Badge>
                      </div>
                      <Progress value={chapter.score} className="h-2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 pt-4">
          <Button variant="outline" onClick={() => navigate("/analytics")}>
            View Full Analytics
          </Button>
          <Button onClick={() => navigate("/simulations")} className="bg-maple hover:bg-maple-600">
            Continue Practicing
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default WeakAreas;
